import React from "react";
import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import Dot from "@Front/Components/Layouts/Legal/LegalInformations/elements/Dot";

import classes from "./classes.module.scss";
import { Routes, buildRoute } from "@Front/Config/Routes";
import { ELegalOptions } from "@Front/Components/LayoutTemplates/DefaultLegalDashboard";
import LinkInline from "../elements/LinkInline";
import { useDeploymentLineLegalPublisher } from "@Front/Services/DeploymentLineLegalPublisher/DeploymentLineLegalPublisherContext";
import {
	UserRightsExerciseModalities,
	RightToInformationAndAccess,
	RightToErasure,
	RightToLimitation,
	RightToObjection,
	RightToPortability,
	RightToWithdrawConsent,
	RightToLodgeComplaint,
	RightToPostMortemDirectives,
} from "./components/UserRightsSections";

/**
 * Privacy Policy Introduction Section
 */
function PrivacyPolicyIntroduction(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H4} color={ETypoColor.TEXT_PRIMARY}>
				Politique de confidentialité
			</Typography>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY} italic>
				Version en vigueur à compter du 24 avril 2024
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les informations suivantes vous sont communiquées afin que vous puissiez prendre connaissance des engagements en matière de
				protection des données à caractère personnel de {pub.publisherLegalName}, qui agit en tant que responsable du traitement pour les
				traitements de données à caractère personnel évoqués dans le présent document.
			</Typography>
		</section>
	);
}

/**
 * Personal Data Section
 */
function PersonalDataSection(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				1. Les données à caractère personnel que nous traitons
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Dans le cadre des traitements de données à caractère personnel, {pub.publisherLegalName} collecte et traite les données suivantes en
				qualité de responsable du traitement :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<Dot>Données d&apos;identité (nom, prénom), d&apos;identification (courriel et numéro de téléphone);</Dot>
				<Dot>Données de paiement;</Dot>
				<Dot>Données de connexion et de navigation.</Dot>
			</Typography>

			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Il peut également arriver que des Internautes communiquent d&apos;autres données en contactant {pub.publisherLegalName} à l&apos;aide de son
				formulaire de contact.
			</Typography>
		</section>
	);
}

/**
 * Processing Purposes Section
 */
function ProcessingPurposesSection(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				2. Les finalités de nos traitements
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les traitements que nous mettons en œuvre le sont pour assurer les finalités suivantes :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<Dot>répondre à vos demandes de contacts;</Dot>
				<Dot>la gestion des demandes de droit d&apos;accès, de suppression, de rectification et d&apos;opposition;</Dot>
				<Dot>vous permettre d&apos;accéder au Site et à la plateforme {pub.platformNameInPrivacyAndCookieText};</Dot>
				<Dot>vous envoyer par courriel nos offres relatives aux Services, des actualités et bonnes pratiques.</Dot>
			</Typography>
		</section>
	);
}

/**
 * Legal Basis Section
 */
function LegalBasisSection(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				3. Les fondements juridiques de nos traitements
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Nous ne mettons en œuvre des traitements de données que si au moins l&apos;une des conditions suivantes est remplie :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<Dot>votre consentement aux opérations de traitement a été recueilli;</Dot>
				<Dot>
					l&apos;existence de notre intérêt légitime, ou de celui d&apos;un tiers, qui justifie que nous mettions en œuvre le traitement de
					données à caractère personnel concerné;
				</Dot>
				<Dot>
					l&apos;exécution d&apos;un contrat qui nous lie à vous nécessite que nous mettions en œuvre le traitement de données à caractère
					personnel concerné.
				</Dot>
				<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
					Les intérêts légitimes qui sont poursuivis par {pub.publisherLegalName} peuvent notamment consister dans le management de son
					activité, le suivi de ses contrats et la réalisation de ses prestations.
				</Typography>
			</Typography>
		</section>
	);
}

/**
 * Data Recipients Section
 */
function DataRecipientsSection(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				4. Les destinataires de vos données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les données à caractère personnel que nous collectons, de même que celles qui sont recueillies ultérieurement, nous sont
				destinées en notre qualité de responsable du traitement.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Nous veillons à ce que seules les personnes habilitées puissent avoir accès à ces données. Nos prestataires de services
				peuvent être destinataires de ces données pour réaliser les prestations que nous leur confions. Certaines données
				personnelles peuvent être adressées à des tiers ou à des autorités légalement habilitées et ce pour satisfaire nos
				obligations légales, réglementaires ou conventionnelles.
			</Typography>
		</section>
	);
}

/**
 * Data Retention Section
 */
function DataRetentionSection(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				5. La durée de conservation de vos données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les durées de conservation que nous appliquons à vos données à caractère personnel sont proportionnées aux finalités pour
				lesquelles elles ont été collectées. En conséquence, nous organisons notre politique de conservation des données de la
				manière suivante :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<u>Durée de conservation des données techniques</u>
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les données techniques sont conservées pour la durée strictement nécessaire à la réalisation des finalités visées dans la
				présente politique de confidentialité.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<u>Conservation des données pendant la durée de la relation contractuelle</u>
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les données à caractère personnel faisant l&apos;objet d&apos;un traitement ne sont pas conservées au-delà du temps nécessaire à
				l&apos;exécution des obligations définies lors de la conclusion du contrat ou de la durée prédéfinie de la relation
				contractuelle.
			</Typography>
		</section>
	);
}

/**
 * Data Security Section
 */
function DataSecuritySection(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				6. La sécurité de vos données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} a mis en œuvre des mesures pour protéger la confidentialité, la sécurité et l&apos;intégrité de vos données à
				caractère personnel. L&apos;accès aux données à caractère personnel est restreint aux employés et fournisseurs de service qui ont
				besoin de connaître ces informations et qui ont été formés pour se conformer aux règles en matière de confidentialité.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} s&apos;engage à ce que vos données à caractère personnel ne soient pas altérées ou endommagées et que des tiers non
				autorisés n&apos;y aient pas accès.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Nous disposons des mesures de protection physiques, techniques, électroniques, procédurales et organisationnelles
				appropriées pour protéger les informations personnelles recueillies contre tout traitement non autorisé.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Toutefois, {pub.publisherLegalName} ne saurait vous garantir contre toute perte, destruction ou dommages de vos données à caractère
				personnel.
			</Typography>
		</section>
	);
}

/**
 * User Rights Section
 */
function UserRightsSection(): React.JSX.Element {
	return (
		<>
			<UserRightsExerciseModalities />
			<RightToInformationAndAccess />
			<RightToErasure />
			<RightToLimitation />
			<RightToObjection />
			<RightToPortability />
			<RightToWithdrawConsent />
			<RightToLodgeComplaint />
			<RightToPostMortemDirectives />
		</>
	);
}

/**
 * Cookies Section
 */
function CookiesSection(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				8. Cookies
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				En naviguant sur notre site, des cookies sont déposés sur votre terminal, soit directement, soit après avoir recueilli votre
				consentement lorsque la réglementation en matière de cookies le requiert.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous êtes invité à vous référer aux
				<LinkInline
					text="Politiques cookies"
					href={buildRoute(Routes.pages.Legal.LegalInformations, { legalUid: ELegalOptions.POLITIQUE_DE_GESTION_DES_COOKIES })}
				/>
				concernant l&apos;utilisation par {pub.platformNameInPrivacyAndCookieText} des cookies.
			</Typography>
		</section>
	);
}

export default function PrivacyPolicy(): React.JSX.Element {
	return (
		<div className={classes["root"]}>
			<PrivacyPolicyIntroduction />
			<PersonalDataSection />
			<ProcessingPurposesSection />
			<LegalBasisSection />
			<DataRecipientsSection />
			<DataRetentionSection />
			<DataSecuritySection />

			<UserRightsSection />
			<CookiesSection />
		</div>
	);
}
